import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Check, HelpCircle, X, CreditCard, Lock, LogIn, Shield, CalendarPlus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { InstallButton } from "@/components/InstallButton";
import { PageLoader, Empty, Pill, copyText } from "@/components/kit";
import { api, errMsg, fmtDate, fmtDay, gbp, initials, onColor } from "@/lib/api";
import { useTeamManifest } from "@/lib/pwa";
import { useAuth } from "@/context/AuthContext";
import { ResultBadge } from "./hub/HubParts";
import { EmbedCard } from "@/components/EmbedFrame";
import { themeClass, themeStyle, themeBg } from "@/lib/theme";

const RSVP = [["yes", "In", Check, "#10B981"], ["maybe", "Maybe", HelpCircle, "#F59E0B"], ["no", "Out", X, "#EF4444"]];

const EventItem = ({ e, onRsvp }) => (
  <div className="rounded-lg border border-white/[0.08] bg-[#111827] p-5" data-testid={`player-event-${e.id}`}>
    <div className="flex items-start justify-between gap-3">
      <div><Pill tone={e.type === "match" ? "volt" : e.type === "training" ? "cyan" : "amber"}>{e.type}</Pill><p className="mt-2 font-display text-2xl font-bold uppercase leading-tight text-white">{e.title}</p><p className="font-mono text-xs text-slate-400">{fmtDate(e.starts_at)}{e.venue && ` · ${e.venue}`}</p></div>
      <p className="shrink-0 font-mono text-xs text-slate-500">{e.going} going</p>
    </div>
    {e.notes && <p className="mt-3 text-sm text-slate-400">{e.notes}</p>}
    <div className="mt-4 grid grid-cols-3 gap-2">
      {RSVP.map(([k, label, Icon, c]) => {
        const on = e.my_status === k;
        return (
          <button key={k} onClick={() => onRsvp(e, k)} data-testid={`rsvp-${k}-${e.id}`} className="flex items-center justify-center gap-1.5 rounded-md border py-2.5 text-sm font-bold uppercase transition-all active:scale-95" style={on ? { background: c, borderColor: c, color: "#0B0F17" } : { borderColor: "var(--hub-border)", color: "var(--hub-text-2)" }}>
            <Icon className="h-4 w-4" /> {label}
          </button>
        );
      })}
    </div>
  </div>
);

const ChargeItem = ({ c, onPay, paying, canPay }) => (
  <div className="flex items-center justify-between gap-3 rounded-lg border border-white/[0.08] bg-[#111827] p-5" data-testid={`player-charge-${c.id}`}>
    <div><p className="font-semibold text-white">{c.title}</p><p className="text-xs text-slate-500">{c.due_date ? `Due ${fmtDay(c.due_date)}` : "No due date"}</p></div>
    <div className="flex items-center gap-3">
      <p className="font-display text-2xl font-black text-white">{gbp(c.amount)}</p>
      {c.status === "paid" ? <Pill tone="green" data-testid={`charge-paid-${c.id}`}>paid</Pill> : !canPay ? <Pill tone="amber" data-testid={`charge-owed-${c.id}`}>pay your admin</Pill> : (
        <Button size="sm" disabled={paying === c.id} onClick={() => onPay(c)} className="rounded-full font-bold" data-testid={`pay-charge-${c.id}`}><CreditCard className="mr-1 h-4 w-4" />{paying === c.id ? "…" : "Pay"}</Button>
      )}
    </div>
  </div>
);

const Section = ({ title, children }) => (
  <section className="space-y-3">
    <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">{title}</p>
    {children}
  </section>
);

const CalendarCard = ({ teamId }) => {
  const [url, setUrl] = useState("");
  useEffect(() => {
    api.get(`/teams/${teamId}/me/calendar`).then((r) => setUrl(r.data.feed_url)).catch(() => {});
  }, [teamId]);
  const reset = async () => {
    if (!window.confirm("Create a new calendar link? The old one will stop working.")) return;
    const { data } = await api.post(`/teams/${teamId}/me/calendar/reset`);
    setUrl(data.feed_url);
    toast.success("New calendar link created");
  };
  if (!url) return null;
  const webcal = url.replace(/^https?:/, "webcal:");
  const google = `https://calendar.google.com/calendar/render?cid=${encodeURIComponent(webcal)}`;
  const btn = "flex items-center justify-center gap-2 rounded-md border py-2.5 text-sm font-semibold transition-transform active:scale-95";
  return (
    <div className="rounded-lg border border-white/[0.08] bg-[#111827] p-5" data-testid="calendar-card">
      <p className="flex items-center gap-2 font-display text-xl font-bold uppercase text-white"><CalendarPlus className="h-5 w-5" style={{ color: "var(--team-ink)" }} /> Sync to your calendar</p>
      <p className="mt-1 text-sm text-slate-400">Every fixture, training and social updates automatically — including changes and cancellations.</p>
      <div className="mt-4 grid grid-cols-2 gap-2">
        <a href={webcal} className={btn} style={{ borderColor: "var(--hub-border)" }} data-testid="calendar-apple-btn">Apple / Outlook</a>
        <a href={google} target="_blank" rel="noreferrer" className={btn} style={{ borderColor: "var(--hub-border)" }} data-testid="calendar-google-btn">Google Calendar</a>
      </div>
      <div className="mt-3 flex items-center justify-between gap-2 text-xs">
        <button className="text-slate-400 underline" onClick={() => copyText(url, "Calendar link copied")} data-testid="calendar-copy-btn">Copy link</button>
        <button className="text-slate-400 underline" onClick={reset} data-testid="calendar-reset-btn">Reset link</button>
      </div>
    </div>
  );
};

const Portal = ({ teamId, slug, color }) => {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const [paying, setPaying] = useState(null);
  const load = useCallback(() => api.get(`/teams/${teamId}/me`).then((r) => setD(r.data)).catch((e) => setErr({ s: e.response?.status, m: errMsg(e) })), [teamId]);
  useEffect(() => {
    load();
  }, [load]);

  const rsvp = async (e, status) => {
    await api.post(`/teams/${teamId}/me/availability/${e.id}`, { status });
    toast.success(status === "yes" ? "You're in!" : status === "no" ? "Marked unavailable" : "Marked as maybe");
    load();
  };
  const pay = async (c) => {
    setPaying(c.id);
    try {
      const { data } = await api.post("/payments/fee-checkout", { charge_id: c.id, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(errMsg(e));
      setPaying(null);
    }
  };

  if (err) return <Empty icon={Lock} title={err.s === 423 ? "Team paused" : "Not on this squad"} text={err.s === 404 ? "Your account isn't a member of this team. Ask the team admin for an invite." : err.m} />;
  if (!d) return <PageLoader />;
  const owed = d.charges.filter((c) => c.status !== "paid").reduce((a, c) => a + c.amount, 0);
  return (
    <div className="space-y-8">
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-lg border border-white/[0.08] bg-[#111827] p-5"><p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">You owe</p><p className="mt-2 font-display text-3xl font-black text-white" data-testid="player-owed-total">{gbp(owed)}</p></div>
        <div className="rounded-lg border border-white/[0.08] bg-[#111827] p-5"><p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Upcoming</p><p className="mt-2 font-display text-3xl font-black text-white">{d.events.length}</p></div>
      </div>
      {d.me.role === "admin" && <Link to={`/manage/${teamId}`} className="flex items-center gap-2 text-sm" style={{ color: "var(--team-ink)" }} data-testid="player-manage-link"><Shield className="h-4 w-4" /> Open team admin</Link>}
      <Section title="Next up — are you available?">{d.events.map((e) => <EventItem key={e.id} e={e} onRsvp={rsvp} />)}{!d.events.length && <p className="text-sm text-slate-500">Nothing scheduled yet.</p>}</Section>
      {!d.me.impersonating && <CalendarCard teamId={teamId} />}
      <Section title="My fees">{d.charges.map((c) => <ChargeItem key={c.id} c={c} onPay={pay} paying={paying} canPay={d.features?.card_payments} />)}{!d.charges.length && <p className="text-sm text-slate-500">No fees assigned to you.</p>}</Section>
      {d.embeds?.length > 0 && <Section title="Forms">{d.embeds.map((e) => <EmbedCard key={e.id} embed={e} />)}</Section>}
      <Section title="Recent results">
        {d.results.map((r) => <div key={r.id} className="flex items-center justify-between rounded-lg border border-white/[0.08] bg-[#111827] p-4"><div className="flex items-center gap-3"><ResultBadge e={r} /><span className="text-white">{r.title}</span></div><span className="font-display text-2xl font-black text-white">{r.our_score}–{r.their_score}</span></div>)}
      </Section>
      <Section title="Team news">
        {d.posts.map((p) => <div key={p.id} className="rounded-lg border border-white/[0.08] bg-[#111827] p-5" data-testid={`player-post-${p.id}`}>{p.visibility === "members" && <Pill tone="amber"><Lock className="h-3 w-3" />squad only</Pill>}<p className="mt-1 font-display text-xl font-bold uppercase text-white">{p.title}</p><p className="mt-1 whitespace-pre-line text-sm text-slate-400">{p.body}</p></div>)}
      </Section>
      <Section title="Squad">
        <div className="flex flex-wrap gap-2">{d.squad.map((m) => <Pill key={m.id} tone={m.role === "admin" ? "volt" : "slate"}>{m.number != null ? `#${m.number} ` : ""}{m.name}</Pill>)}</div>
      </Section>
      <Link to={`/t/${slug}`} className="block text-center text-sm text-slate-500 hover:text-white">View public fan hub →</Link>
    </div>
  );
};

export default function PlayerPortal() {
  const { slug } = useParams();
  const { user, logout } = useAuth();
  const [hub, setHub] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    api.get(`/public/teams/${slug}`).then((r) => setHub(r.data)).catch((e) => setError(errMsg(e)));
  }, [slug]);
  const team = hub?.team;
  const color = team?.primary_color || "#CCFF00";
  useTeamManifest(hub ? slug : null, "player", themeBg(team?.theme), team ? `${team.name} Players` : null, (team?.logo_url || "").slice(-12));

  if (error) return <div className="mx-auto max-w-md px-4 py-24"><Empty title="Team not found" text={error} /></div>;
  if (!hub || user === null) return <PageLoader />;
  return (
    <div className={`min-h-screen bg-[#0B0F17] ${themeClass(team.theme)}`} style={themeStyle(team.theme, color)} data-testid="player-portal">
      <header className="sticky top-0 z-50 border-b border-white/10 bg-[#0B0F17]/85 backdrop-blur-xl" style={{ borderTop: `3px solid ${color}` }}>
        <div className="mx-auto flex h-16 max-w-md items-center justify-between gap-3 px-4">
          <div className="flex min-w-0 items-center gap-3">
            {team.logo_url ? <img src={team.logo_url} alt={team.name} className="h-9 w-9 shrink-0 rounded object-contain" /> : <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded font-display font-black" style={{ background: color, color: onColor(color) }}>{initials(team.name)}</span>}
            <div className="min-w-0"><p className="truncate font-display text-lg font-black uppercase leading-none text-white">{team.name}</p><p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Player app</p></div>
          </div>
          <InstallButton label="Install" color="var(--team-ink)" testid="player-install-btn" />
        </div>
      </header>
      <main className="mx-auto max-w-md px-4 py-6">
        {hub.paused ? <Empty icon={Lock} title="Team paused" text="This team is temporarily unavailable." /> : !user ? (
          <Empty icon={LogIn} title="Sign in to your squad" text="Use the email your team admin invited. Check your inbox for the registration link if you haven't set a password yet.">
            <Button asChild className="rounded-full font-bold" data-testid="player-login-btn"><Link to={`/login?next=/t/${slug}/players`}>Log in</Link></Button>
          </Empty>
        ) : (
          <>
            <p className="mb-6 text-sm text-slate-400">Hi <span className="font-semibold text-white">{user.name}</span> · <button className="underline" onClick={logout} data-testid="player-logout-btn">log out</button></p>
            <Portal teamId={team.id} slug={slug} color={color} />
          </>
        )}
      </main>
    </div>
  );
}
